import type {
  XinyiAbout,
  XinyiFAQItem,
  XinyiGuide,
  XinyiHero,
  XinyiHowToGetHere,
  XinyiInfo,
  XinyiResearchItem,
} from './types'

/* ============================================================
 * XinyiStoreV2Page — central content (zh / en)
 * Mirrors the SEO-critical copy from XinyiStorePage, but
 * adapted for Barista Map V2 layout (single big-word hero,
 * asymmetrical splits, minimal expand cards, FAQ).
 * ============================================================ */

export const xinyiHero: XinyiHero = {
  image: 'https://file.lumi.new/p373839333138763776/SPF2017537549456433152.webp',
  imageAlt: {
    zh: 'ONGU Studio 信義店外觀與烘豆吧台',
    en: 'ONGU Studio Xinyi storefront and roasting bar',
  },
  title: {
    zh: '信義 / Xinyi',
    en: 'Xinyi',
  },
  seoH1: {
    zh: '信義區咖啡廳推薦｜ONGU Studio 信義｜自家烘焙・職人手沖',
    en: 'Best coffee shop in Xinyi District｜ONGU Studio Xinyi｜House-roasted coffee & artisan pour-overs',
  },
}

export const xinyiAbout: XinyiAbout = {
  eyebrow: { zh: 'About', en: 'About' },
  headline: {
    zh: 'Ongu Studio - 信義',
    en: 'Ongu Studio – Xinyi',
  },
  quote: {
    zh: '「我們想在台北創造美好的咖啡日常。」',
    en: '"We want to create a beautiful coffee everyday in Taipei."',
  },
  body: {
    zh: '專注於咖啡烘焙與沖煮，將熱情注入每一杯，讓停下腳步的片刻充滿放鬆與鼓舞。研究、品質、烘焙與沖煮，是我們在信義商圈做現場烘培的不二法門。',
    en: 'Focused on coffee roasting and brewing, we pour our passion into every cup, making every pause filled with relaxation and uplift. Research, quality, roasting and brewing are the foundations behind our on-site roasting in the heart of Xinyi.',
  },
  image: 'https://file.lumi.new/p373839333138763776/SPF2021991565232693248.webp',
  imageAlt: {
    zh: 'ONGU Studio 信義店吧台與咖啡製作',
    en: 'ONGU Studio Xinyi bar and coffee preparation',
  },
}

export const xinyiInfo: XinyiInfo = {
  eyebrow: { zh: 'Visit', en: 'Visit' },
  title: {
    zh: '營業資訊',
    en: 'Visit Us',
  },
  address: {
    zh: '台北市信義區信義路四段389號1樓',
    en: '1F, No.389, Sec.4, Xinyi Rd., Xinyi District, Taipei, Taiwan',
  },
  addressUrl: 'https://maps.app.goo.gl/miaiuCniLAJrPCGZ6',
  hours: [
    { zh: '週一至五  09:00 – 17:00', en: 'Mon – Fri  09:00 – 17:00' },
    { zh: '週六  10:00 – 17:00', en: 'Saturday  10:00 – 17:00' },
    { zh: '週日公休', en: 'Sunday  Closed' },
  ],
  phone: '+886 938 510 307',
  instagram: '@onguroasters',
  instagramUrl: 'https://www.instagram.com/onguroasters/',
  image: 'https://file.lumi.new/p373839333138763776/SPF1986472180095631360.jpg',
  imageAlt: {
    zh: 'ONGU Studio 信義店空間與工業風吧台',
    en: 'ONGU Studio Xinyi space and industrial bar',
  },
}

export const xinyiHowToGetHere: XinyiHowToGetHere = {
  eyebrow: { zh: 'Directions', en: 'Directions' },
  title: { zh: 'How to get here', en: 'How to get here' },
  paragraphs: [
    {
      zh: 'ONGU Studio 位於台北市信義區信義路四段389號，屬於信義商圈步行範圍。',
      en: 'ONGU Studio is located at No. 389, Sec. 4, Xinyi Road, Xinyi District, Taipei, within walking distance of the Xinyi shopping district.',
    },
    {
      zh: '若你在台北 101 或信義威秀一帶，沿著信義路步行前往，約 5–8 分鐘即可抵達。',
      en: 'If you are around Taipei 101 or Vieshow Cinemas, walk along Xinyi Road and you can arrive in about 5–8 minutes.',
    },
    {
      zh: '從信義威秀步行約 5 分鐘，距離台北 101 約 500 公尺，很適合在信義商圈逛街、辦公途中順路外帶一杯咖啡或停下來品飲季節手沖。',
      en: 'About 5 minutes walk from Vieshow Cinemas, around 500 meters from Taipei 101 — perfect for grabbing a takeaway coffee or stopping by for a seasonal pour-over while shopping or working in Xinyi.',
    },
  ],
  links: [
    {
      label: { zh: 'Google 地圖一鍵開啟', en: 'Open in Google Maps' },
      href: 'https://maps.app.goo.gl/miaiuCniLAJrPCGZ6',
    },
    {
      label: { zh: '從台北 101 導航到這裡', en: 'Navigate from Taipei 101' },
      href: 'https://www.google.com/maps/dir/台北101/25.033,121.565',
    },
    {
      label: { zh: '從信義威秀導航到這裡', en: 'Navigate from Vieshow Cinemas' },
      href: 'https://www.google.com/maps/dir/信義威秀/25.033,121.565',
    },
  ],
  mapEmbedUrl:
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3615.0234567890123!2d121.56300000000001!3d25.033!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjXCsDAxJzU4LjgiTiAxMjHCsDMzJzU0LjAiRQ!5e0!3m2!1sen!2stw!4v1234567890123!5m2!1sen!2stw',
  mapTitle: {
    zh: 'ONGU Studio 信義店地圖',
    en: 'ONGU Studio Xinyi Map',
  },
}

export const xinyiGuide: XinyiGuide = {
  eyebrow: { zh: 'Quick Guide', en: 'Quick Guide' },
  title: {
    zh: '信義區咖啡快速指南',
    en: 'Xinyi Coffee Quick Guide',
  },
  rows: [
    {
      label: { zh: '店名', en: 'Store Name' },
      value: {
        zh: 'Ongu Studio - Coffee Roasting - 信義',
        en: 'Ongu Studio - Coffee Roasting - Xinyi',
      },
      href: 'https://maps.app.goo.gl/miaiuCniLAJrPCGZ6',
    },
    {
      label: { zh: '定位', en: 'Positioning' },
      value: {
        zh: '信義區咖啡廳推薦｜職人手沖・咖啡烘培',
        en: 'Xinyi Specialty Coffee｜Artisan Pour-over & Coffee Roasting',
      },
    },
    {
      label: { zh: '特色', en: 'Features' },
      value: {
        zh: '咖啡烘焙｜季節手沖｜全不鏽鋼工業設計｜座位精簡（適合外帶 / 短停品飲）',
        en: 'Coffee Roasting｜Seasonal Pour-over｜Full Stainless Steel Industrial Design｜Limited Seating (Ideal for takeaway / brief tasting)',
      },
    },
    {
      label: { zh: '適合誰', en: 'Perfect For' },
      value: {
        zh: '想找信義區手沖、信義區外帶咖啡、信義區自家烘焙咖啡豆的人',
        en: 'Those seeking pour-over coffee, takeaway coffee, or house-roasted beans in Xinyi District',
      },
    },
  ],
  ctas: [
    {
      label: { zh: '選購咖啡豆', en: 'Shop Coffee Beans' },
      href: '/shop/beans/v2',
    },
    {
      label: { zh: '選購濾掛 / 掛耳', en: 'Shop Drip Bags' },
      href: '/shop/dripbag/v2',
    },
    {
      label: { zh: '查看所有門市', en: 'View All Locations' },
      href: '/store/v2',
    },
  ],
}

export const xinyiResearchItems: XinyiResearchItem[] = [
  {
    tag: 'Research',
    title: { zh: '研究', en: 'Research' },
    body: {
      zh: '我們在科學、自然與文化之間，持續探索咖啡的更多可能。\n以開放的視角看待咖啡產業，透過不斷的創造與學習，\n希望讓更多人產生深入理解與探索咖啡的動力。',
      en: 'Between science, nature, and culture, we continuously explore the endless possibilities of coffee.\nWith an open perspective on the coffee industry, through constant creation and learning,\nwe hope to inspire more people to deeply understand and explore coffee.',
    },
    image: 'https://file.lumi.new/p373839333138763776/SPF2021991565719232512.webp',
    imageAlt: {
      zh: 'ONGU Studio 咖啡研究與沖煮細節',
      en: 'ONGU Studio coffee research and brewing details',
    },
    mediaPosition: 'left',
  },
  {
    tag: 'Quality',
    title: { zh: '品質', en: 'Quality' },
    body: {
      zh: '我們專注提供高品質、乾淨且甜度良好的咖啡豆，\n減少烘焙賦予的風味，以平衡、柔和為主軸，保留產地特色，\n讓風味清晰、容易理解，也能自然融入日常。',
      en: 'We focus on high-quality, clean coffee beans with excellent sweetness.\nReducing roast-imparted flavors, we emphasize balance and gentleness while preserving origin characteristics,\nmaking flavors clear, easy to understand, and naturally integrated into daily life.',
    },
    image: 'https://file.lumi.new/p373839333138763776/SPF2021991565232693248.webp',
    imageAlt: {
      zh: 'ONGU Studio 咖啡吧台與品質展示',
      en: 'ONGU Studio bar and quality showcase',
    },
    mediaPosition: 'right',
  },
  {
    tag: 'Roasting & Brewing',
    title: { zh: '烘焙與沖煮', en: 'Roasting & Brewing' },
    body: {
      zh: '我們在不同品種、處理法與烘焙樣態之間反覆探索，\n在沖煮中持續實驗與創新；透過水質、研磨、沖煮架構與萃取條件的調整，\n讓每一杯咖啡都帶著清楚的脈絡，輕柔地走進日常。',
      en: 'We explore varieties, processing methods and roast profiles, while experimenting in brewing.\nThrough adjustments in water, grind, brew structure and extraction,\neach cup carries a clear narrative and quietly enters daily life.',
    },
    image: 'https://file.lumi.new/p373839333138763776/SPF2021991564637102080.webp',
    imageAlt: {
      zh: 'ONGU Studio 手沖咖啡過程',
      en: 'ONGU Studio pour-over coffee process',
    },
    mediaPosition: 'left',
  },
]

export const xinyiFAQ: XinyiFAQItem[] = [
  {
    q: {
      zh: 'ONGU Studio 信義店算信義區咖啡廳推薦嗎？特色是什麼？',
      en: 'Is ONGU Studio Xinyi a recommended Xinyi cafe? What makes it different?',
    },
    a: {
      zh: '是的，ONGU Studio 信義店主打「職人現場烘豆＋季節手沖」與金屬工業設計空間。座位不多，但能近距離看見製作過程，適合想在信義區喝到乾淨、層次清晰風味的人。',
      en: 'Yes — ONGU Studio Xinyi is centered on on-site roasting and seasonal pour-overs, set inside a stainless-steel industrial space. Limited seating but a clear view of the craft, ideal for those seeking clean, layered specialty coffee in Xinyi.',
    },
  },
  {
    q: {
      zh: '這裡比較適合外帶還是內用？有座位嗎？',
      en: 'Is it better for takeaway or dine-in? Do you have seating?',
    },
    a: {
      zh: '店內座位精簡，保留寬敞的職人操作區與展示區。你可以內用短停品飲，也很適合在信義區外帶咖啡、順路帶走一杯手沖或奶咖。',
      en: 'Seating is limited so we can keep a generous craft and display area. You are welcome to enjoy a quick tasting on-site, or grab a pour-over / milk drink to go.',
    },
  },
  {
    q: {
      zh: '信義店有現場烘豆嗎？每天都有嗎？',
      en: 'Do you really roast on-site? Every day?',
    },
    a: {
      zh: '有的，ONGU 信義店以現場烘豆為核心特色之一。烘豆師會監控溫度、時間、氣流與顏色變化，讓你能聞到烘焙過程的香氣與節奏。',
      en: 'Yes — on-site roasting is one of our core features. The roaster monitors temperature, time, airflow and color, so you can experience the aromas and rhythm of every batch.',
    },
  },
]
